import React, { useMemo, useRef, useState, useLayoutEffect } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { getColumnId } from '@/types/table.types';

const SKELETON_BAR_FRACTION = 0.7;

export interface GenericTableSkeletonConfig {
  storageKey: string;
  defaultWidth: number;
  rowHeight: number;
  headerRowHeightPx: number;
  /** Optional hardcoded default widths by column ID */
  defaultWidths?: Record<string, number>;
}

/* ---------- Storage helpers (generic, merge-max strategy) ---------- */

// eslint-disable-next-line react-refresh/only-export-components -- factory helper paired with the skeleton component in this file
export const createStorageHelpers = (storageKey: string) => ({
  getStoredWidths: (): Record<string, number> => {
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) return JSON.parse(stored);
    } catch {
      // ignore
    }
    return {};
  },
  storeColumnWidths: (widths: Record<string, number>): void => {
    try {
      const existing = localStorage.getItem(storageKey);
      const parsed: Record<string, number> = existing ? JSON.parse(existing) : {};
      const merged = { ...parsed };
      for (const [key, value] of Object.entries(widths)) {
        merged[key] = Math.max(merged[key] ?? 0, value);
      }
      localStorage.setItem(storageKey, JSON.stringify(merged));
    } catch {
      // ignore
    }
  },
});

/* ---------- Internal helpers ---------- */

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getHeaderText = (col: ColumnDef<any, any>): string => {
  const raw = col.header;
  if (raw === undefined || raw === null) return 'Column';
  if (typeof raw === 'function') {
    const node = (raw as () => React.ReactNode)();
    if (typeof node === 'string') return node;
    return '';
  }
  return String(raw);
};

/* ---------- Component ---------- */

type GenericTableSkeletonProps = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  columns: ColumnDef<any, any>[];
  config: GenericTableSkeletonConfig;
};

export const GenericTableSkeleton: React.FC<GenericTableSkeletonProps> = ({ columns, config }) => {
  const { storageKey, defaultWidth, rowHeight, headerRowHeightPx, defaultWidths } = config;

  const containerRef = useRef<HTMLDivElement>(null);
  const [rowCount, setRowCount] = useState(30);

  const storedWidths = useMemo(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) return JSON.parse(stored) as Record<string, number>;
    } catch {
      // ignore
    }
    return {} as Record<string, number>;
  }, [storageKey]);

  const columnWidths = useMemo(
    () =>
      columns.map((col) => {
        const cid = getColumnId(col);
        const hardcoded = defaultWidths?.[cid];
        const fallback = cid === 'rowNumber' ? 40 : defaultWidth;
        return storedWidths[cid] ?? hardcoded ?? fallback;
      }),
    [columns, storedWidths, defaultWidth, defaultWidths],
  );

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const available = el.clientHeight - headerRowHeightPx;
    setRowCount(Math.max(1, Math.ceil(available / rowHeight)));
  }, [headerRowHeightPx, rowHeight]);

  return (
    <div ref={containerRef} className="flex flex-1 flex-col overflow-auto">
      <div className="flex flex-1 flex-col" style={{ minWidth: 'max-content' }}>
        {/* Header */}
        <div
          className="sticky top-0 z-10 flex items-center border-b border-gray-200 bg-gray-50 px-4 py-2 text-sm font-medium text-gray-900"
          style={{ height: `${headerRowHeightPx}px`, minHeight: `${headerRowHeightPx}px` }}
        >
          {columns.map((col, i) => {
            const w = columnWidths[i];
            return (
              <div
                key={getColumnId(col)}
                className="mx-1.5 flex min-h-0 flex-shrink-0 items-center line-clamp-2 break-words"
                style={{ width: `${w}px`, minWidth: `${w}px` }}
                title={getHeaderText(col)}
              >
                {getHeaderText(col)}
              </div>
            );
          })}
        </div>

        {/* Skeleton rows */}
        <div className="flex-1">
          {Array.from({ length: rowCount }).map((_, rowIdx) => (
            <div
              key={rowIdx}
              className="flex items-center border-b border-gray-200 px-4 py-2"
              style={{ height: `${rowHeight}px` }}
            >
              {columns.map((col, i) => {
                const w = columnWidths[i];
                const barWidth = Math.round(w * SKELETON_BAR_FRACTION);
                return (
                  <div
                    key={getColumnId(col)}
                    className="mx-1.5 flex flex-shrink-0 items-center"
                    style={{ width: `${w}px`, minWidth: `${w}px` }}
                  >
                    <div
                      className="h-3.5 flex-shrink-0 animate-pulse rounded bg-gray-200"
                      style={{ width: `${barWidth}px` }}
                    />
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
